import { db } from "@/api/firebase/firebase";
import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { TransactionGroup, Transaction } from "@/types/TransactionGroup";

const COLLECTION_NAME = "transactionGroups";

export class TransactionGroupService {
  static async getAll(): Promise<TransactionGroup[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as TransactionGroup[];
    } catch (error) {
      console.error("Error getting transaction groups:", error);
      throw error;
    }
  }

  static async get(id: string): Promise<TransactionGroup> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Transaction group not found");
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name,
        description: data.description,
        transactions: data.transactions || [],
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        isDefault: data.isDefault,
      } as TransactionGroup;
    } catch (error) {
      console.error("Error getting transaction group:", error);
      throw error;
    }
  }

  static async create(
    data: Omit<TransactionGroup, "id" | "createdAt" | "updatedAt">
  ): Promise<TransactionGroup> {
    try {
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...data,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      const newDoc = await getDoc(docRef);
      return {
        id: docRef.id,
        ...newDoc.data(),
        createdAt: newDoc.data()?.createdAt.toDate(),
        updatedAt: newDoc.data()?.updatedAt.toDate(),
      } as TransactionGroup;
    } catch (error) {
      console.error("Error creating transaction group:", error);
      throw error;
    }
  }

  static async update(
    id: string,
    data: Partial<TransactionGroup>
  ): Promise<TransactionGroup> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now(),
      });

      // Fetch and return the updated document
      return await this.get(id);
    } catch (error) {
      console.error("Error updating transaction group:", error);
      throw error;
    }
  }

  static async delete(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting transaction group:", error);
      throw error;
    }
  }

  static async addTransaction(
    groupId: string,
    transaction: Transaction
  ): Promise<TransactionGroup> {
    try {
      const group = await this.get(groupId);
      const updatedTransactions = [...group.transactions, transaction];

      await this.update(groupId, {
        transactions: updatedTransactions,
      });

      return await this.get(groupId);
    } catch (error) {
      console.error("Error adding transaction:", error);
      throw error;
    }
  }

  static async removeTransaction(
    groupId: string,
    transactionId: string
  ): Promise<TransactionGroup> {
    try {
      const group = await this.get(groupId);
      const updatedTransactions = group.transactions.filter(
        (t) => t.id !== transactionId
      );

      await this.update(groupId, {
        transactions: updatedTransactions,
      });

      return await this.get(groupId);
    } catch (error) {
      console.error("Error removing transaction:", error);
      throw error;
    }
  }

  static async updateTransaction(
    groupId: string,
    transactionId: string,
    updatedTransaction: Transaction
  ): Promise<TransactionGroup> {
    try {
      const group = await this.get(groupId);
      const updatedTransactions = group.transactions.map((t) =>
        t.id === transactionId ? updatedTransaction : t
      );
      return await this.update(groupId, { transactions: updatedTransactions });
    } catch (error) {
      console.error("Error updating transaction:", error);
      throw error;
    }
  }
}
